import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';

const PopulateTemplateChannelsVariablesSchema = z.object({
  templateId: z.string(),
  channels: z.array(z.string()),
});

export type PopulateTemplateChannelsVariables = z.infer<
  typeof PopulateTemplateChannelsVariablesSchema
>;

async function populateTemplateChannelsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: PopulateTemplateChannelsVariables,
) {
  const { templateId, channels } = PopulateTemplateChannelsVariablesSchema.parse(variables);
  const instance = await axiosInstance;

  return instance.post(`/templates/${templateId}/bodies`, { channels });
}

export function usePopulateTemplateChannels() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<unknown, ApiError, PopulateTemplateChannelsVariables>({
    mutationFn: variables => populateTemplateChannelsFn(axiosInstance, variables),
    onSuccess: (_data, { templateId }) => {
      client.invalidateQueries({ queryKey: ['template-v2', templateId] });
      client.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return addActionNameToMutationResult<
    unknown,
    ApiError,
    PopulateTemplateChannelsVariables,
    'populateTemplateChannels'
  >('populateTemplateChannels', mutation);
}
